import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useContactInformationFormFields } from "@apac/hooks";
import type {
  IContactInfo as ContactInfoFormContext,
  IContactInfo,
} from "@b2bportal/core-types";
import { debounce } from "lodash-es";

export interface UseContactInformationFormParams {
  initialContactInfo?: IContactInfo;
  onChange: (contactInfo: IContactInfo) => void;
}

export const useContactInformationForm = ({
  onChange,
  initialContactInfo,
}: UseContactInformationFormParams) => {
  const { formFields, contactInfoDefaultValues } =
    useContactInformationFormFields();

  const formContext = useForm<ContactInfoFormContext>({
    defaultValues: {
      countryCode:
        initialContactInfo?.countryCode ?? contactInfoDefaultValues.countryCode,
      phoneNumber:
        initialContactInfo?.phoneNumber ?? contactInfoDefaultValues.phoneNumber,
      email: initialContactInfo?.email ?? contactInfoDefaultValues.email,
    },
    mode: "onChange",
  });

  const { register, watch } = formContext;

  for (const [name, details] of Object.entries(formFields)) {
    register(name as keyof ContactInfoFormContext, details);
  }

  const debouncedOnContactInfoChange = debounce(onChange, 500);

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      debouncedOnContactInfoChange(value as ContactInfoFormContext);
    });

    return () => unsubscribe();
  }, [debouncedOnContactInfoChange, watch]);

  return {
    context: { formContext, contactInfoDefaultValues },
  };
};
